import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { CircularProgress } from "@mui/material";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Paper from "@mui/material/Paper";
import Popper, { PopperPlacementType } from "@mui/material/Popper";
import Tooltip from "@mui/material/Tooltip";
import * as React from "react";

type SplitButtonOption = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tooltip?: string;
};

type SplitButtonProps = {
  color?: any;
  size?: "small" | "medium" | "large";
  buttonText: string;
  onButtonClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  options: SplitButtonOption[];
  variant?: "text" | "outlined" | "contained";
  withBackground?: boolean;
  placement?: PopperPlacementType;
  mainButtonToggleMenu?: boolean;
};

export function SplitButton({
  color,
  size = "small",
  buttonText,
  placement,
  onButtonClick,
  options,
  disabled,
  loading,
  variant,
  withBackground,
  mainButtonToggleMenu,
}: SplitButtonProps) {
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);

  const hasDivider = variant === "contained" || !variant;

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  const handleOptionClick = (option: SplitButtonOption) => {
    setOpen(false);
    option.onClick();
  };

  return (
    <React.Fragment>
      <ButtonGroup
        color={color ? color : "inherit"}
        sx={{ border: "none", boxShadow: "none" }}
        size={size}
        disabled={disabled || loading}
        variant={variant ? variant : "contained"}
        ref={anchorRef}
      >
        <Button
          sx={{
            padding: "6px 12px 6px 16px",
            backgroundColor: color ? color : "",
            borderRight: "0px !important",
          }}
          color={color}
          onClick={mainButtonToggleMenu ? handleToggle : onButtonClick}
        >
          {loading ? (
            <CircularProgress color="inherit" size={20} />
          ) : (
            buttonText
          )}
        </Button>
        <Button
          aria-controls={open ? "split-button-menu" : undefined}
          aria-expanded={open ? "true" : undefined}
          aria-label="select an option"
          aria-haspopup="menu"
          color={color}
          sx={{
            ...(hasDivider ? { borderLeft: "1px solid" } : { borderLeft: 0 }),
            padding: "0 8px",
            backgroundColor: color ? color : "",
            "&:hover": {
              borderLeft: hasDivider ? "1px solid" : 0,
            },
          }}
          onClick={handleToggle}
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Popper
        sx={{
          zIndex: 1,
          minWidth: "120px",
        }}
        open={open}
        anchorEl={anchorRef.current}
        placement={placement || "bottom"}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <Paper sx={{ background: withBackground ? "#E8E8E8" : "" }}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu" autoFocusItem>
                  {options.map((option, index) => (
                    <Tooltip key={index} title={option.tooltip || ""}>
                      <MenuItem
                        style={{ padding: "6px 16px" }}
                        disabled={option.disabled || loading}
                        onClick={() => handleOptionClick(option)}
                      >
                        {option.label}
                      </MenuItem>
                    </Tooltip>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </React.Fragment>
  );
}
